/**
 * The mixer's public surface.
 *
 * Gameplay only ever sees this interface: it names *what happened*, never how
 * it should sound. `WebAudioManager` turns those events into synthesised audio
 * (src/audio/synth.ts) and spoken lines (src/audio/Voice.ts); `NullAudioManager`
 * records them and stays silent, which is what the tests use.
 */

import type { VoiceStyle } from '../config/audio';

/**
 * Fired once per event.
 *
 * The array is the source of truth rather than the union: a recorded sample is
 * matched to a cue by *filename* (src/audio/samples.ts), so the set of legal
 * names has to exist at runtime for a typo to be catchable.
 */
export const ONE_SHOT_SOUNDS = [
  'footstep',
  'jump',
  /**
   * The second press, on the step it lands inside its window. A cue of its
   * own rather than a second `jump`, because it is the only confirmation the
   * player gets that a timing window they cannot see was hit - see
   * `DOUBLE_JUMP`. Pitched above the take-off it answers.
   */
  'double-jump',
  'land',
  'door-open',
  /**
   * The same leaf going back the other way, and deliberately not `door-open`
   * played backwards. An opening is a latch and then the hinge; a closing is
   * the hinge and then the leaf arriving - the thump into the karmi and the
   * latch dropping after it, which is the half of a door the ear actually
   * uses to know it is shut. See `Door` for why there is a second half at all.
   */
  'door-close',
  'bump',
  'stumble',
  'beer-sip',
  'checkpoint',
  'finish',
  'shout-finaali',
  'hannu-emerge',
  'hannu-shout',
  'hannu-swipe',
  /**
   * The cook at the muurikka, hailing Kimmo across the navetta's front yard.
   * A fallback like every other cue here - `voice/kokki` is what actually says
   * the words, and this is what is left on a machine with no speech synthesis
   * installed. See `KOKKI_LINE`.
   */
  'shout-kokki',
  /**
   * Mäysä in the porstua, telling Kimmo where breakfast is. Same job as
   * `shout-kokki`: the wordless stand-in for `voice/maysa` on a machine with no
   * speech synthesis. See `MAYSA_LINE`.
   */
  'shout-maysa',
  /**
   * The porukka in the juhlasali's last corridor, asking Kimmo whether he
   * really came to play sober. Same job as the two above: the wordless
   * stand-in for `voice/porukka` on a machine with no speech synthesis. See
   * `PORUKKA_LINE`.
   */
  'shout-porukka',
  /**
   * The whole party in front of the XLMOTO-teltta, on the subject of who just
   * took the music off. Same job again - the wordless stand-in for
   * `voice/teltta` where there is no speech synthesis to say the words. See
   * `TELTTA_LINE`.
   */
  'shout-teltta',
  /**
   * Kempf at the sauna door, telling whoever just tried the handle that the
   * sauna is taken. Same job as the four above - the wordless stand-in for
   * `voice/kempf` where there is no speech synthesis - and the only one of
   * them that is not a shout: he is a metre away, and the synthesised version
   * says the same two syllables at a mumble. See `KEMPF_LINE`.
   */
  'shout-kempf',
  /**
   * And the handle he is standing beside: a latch going nowhere twice and the
   * leaf thumping its frame. The one door noise in the game that is not
   * `door-open`, because it is the one door in the game that does not open -
   * and it is what tells a player the press registered at all, a beat before
   * Kempf gets round to explaining. See src/world/Sauna.ts.
   */
  'ovi-lukossa',
  /**
   * The two halves of the drink they want to see: an empty tuoppi lifted off
   * the stack at the end of the bar, and the same glass filling under a hana.
   *
   * A pair like the klapi's below, and deliberately nothing like it. The wood
   * is two blunt knocks; this is glass on a table and then two full seconds of
   * pouring - by a distance the longest one-shot in the game, because it is
   * the only cue that is a *process* rather than an impact, and because the
   * process is the toll. It is cued by the press that opens the handle and it
   * ends where `SIMA.pourDuration` does, so a player who has run out of
   * patience can hear how much of the wait is left. See src/config/sima.ts.
   */
  'tuoppi-poydalta',
  /** The released beer bottle hitting the floor after taking the mug. */
  'bottle-break',
  'hana-auki',
  /**
   * A makkara landing on the plate, and a mouthful of parsakaalia not staying
   * down. The two answers the muurikka gives, and the only feedback a player
   * gets in the tenth of a second before the boxes go again - so they are
   * deliberately nothing like each other.
   */
  'makkara-lautaselle',
  'oksennus',
  /** A klapi off the top of the kasa, and the same klapi into the pesä. */
  'klapi-nosto',
  'klapi-pesaan',
  'power-off',
  /**
   * The pane in Einon pirtti's window, on the step Kimmo goes through it. See
   * src/world/Ikkuna.ts - the glass is scenery, so this is the only thing in
   * the game that says it was ever there.
   */
  'glass-break',
  /**
   * Kimmo, a beat later, on the subject of having gone through it. His
   * response to the glass, separate from the sound of getting out of bed.
   */
  'kimmo-shout',
  /** Kimmo getting out of bed; recorded only. */
  'kimmo-wake',
  /** Kimmo reacting to the blackout; recorded only. */
  'kimmo-oho',
  /**
   * Somebody in the juhlasali, fifteen seconds into the clip on the
   * valkokangas. Like `karaoke` below it has no synthesised version and never
   * will - it is a recording of a man saying a specific thing about a specific
   * play, or it is nothing at all. See src/render/Projection.ts.
   */
  'ajotus',
  /**
   * Kimmo, asleep, on the main screen, every `SNORE.fartInterval` seconds for
   * as long as the snoring is going. A cue of its own rather than something
   * baked into `ambient-snoring`, because the loop is 4.4 seconds long and a
   * joke that lands every 4.4 seconds is not one - see `refreshScreens`.
   */
  'fart',
] as const;

export type OneShotSound = (typeof ONE_SHOT_SOUNDS)[number];

/**
 * Runs until stopped.
 *
 * `karaoke` and `teltta-dj` are the two cues with no synthesised version: one
 * is somebody at the juhlasali's mic and the other is a set out of a PA in the
 * yard, and either is a recording or it is nothing.
 *
 * Three of them have a place in the world - see `setListener` - and each is
 * placed by a sum of its own, because they are three different distances: the
 * karaoke is indoors two rooms away, the tent is out in the open a hundred
 * metres off, and the muurikka is a hiss you can walk out of range of.
 *
 * `running` is the player's own feet. It has a pace rather than a place - see
 * `setPace`.
 */
export const LOOP_SOUNDS = [
  'ambient-morning',
  'ambient-snoring',
  'running',
  'karaoke',
  'teltta-dj',
  /**
   * A metre of steel plate over a gas ring on the kivetys, with the ruokatiimi's
   * breakfast on it. Placed like the two above and unlike either of them in
   * range: it is a *sizzle*, so it carries about as far as a conversation does
   * and there is nothing of it to hear until the veräjä is behind you - see
   * `MUURIKKA_SIHINA`.
   *
   * Named for the pan rather than for the grill it obviously is, because the
   * yard already has a `grilli` and it is a different object: a collider in the
   * pocket behind the ansalanka's row, twenty-six metres west of this one. See
   * src/config/level.ts.
   */
  'muurikka',
] as const;

export type LoopSound = (typeof LOOP_SOUNDS)[number];

export type GameSound = OneShotSound | LoopSound;

/** How the recorded material is doing. Drives the F3 line and nothing else. */
export interface SampleStatus {
  /** Files found in src/audio/samples. */
  total: number;
  /** Decoded and ready to fire on the exact frame they are asked for. */
  ready: number;
  /** Gave up on these - the synthesised or spoken fallback covers them. */
  failed: number;
}

/** A line somebody yells out loud. */
export interface SpokenLine extends VoiceStyle {
  /**
   * Identity of the line. Asking for a line that is already being spoken is
   * ignored, so a repeated event cannot make the speaker stutter.
   */
  id: string;
  text: string;
  /** Played instead when the browser cannot speak (no TTS voices installed). */
  cue?: OneShotSound;
}

export interface AudioManager {
  play(sound: OneShotSound, volume?: number): void;
  /** Speaks a line out loud. Interrupts whatever else was being said. */
  speak(line: SpokenLine): void;
  isSpeaking(id: string): boolean;
  startLoop(sound: LoopSound): void;
  stopLoop(sound: LoopSound): void;
  /**
   * Where the player's ears are, in world XZ.
   *
   * The mixer has no other way to know: nothing else it is told carries a
   * position. Loops that are a *thing in a room* rather than the room itself -
   * the karaoke two doors away - are mixed off this, and everything else
   * ignores it. Rendering may call it every frame; it is only ever read.
   */
  setListener(x: number, z: number): void;
  /**
   * How fast the player is moving along the ground, in m/s.
   *
   * The counterpart of `setListener` for the one loop that is neither a room
   * nor a thing standing in one: the run resamples to this, so the feet keep up
   * with the vauhti meter. Rendering may call it every frame.
   */
  setPace(speed: number): void;
  /** Stops every loop and shuts the speaker up. One-shots ring out. */
  stopAll(): void;
  /**
   * Shuts the speaker up without touching the loops. For a reset that lands
   * back on the screen it started from: the room it is standing in has not
   * changed, so its ambience should not be restarted underneath it.
   */
  stopSpeech(): void;
  setEnabled(enabled: boolean): void;
  /** Freezes the whole mixer mid-sound, for the pause menu. */
  setSuspended(suspended: boolean): void;
  /** Recorded-sample loading, for the debug overlay. */
  sampleStatus(): SampleStatus;
  dispose(): void;
}

/** Records calls (useful in tests / the debug overlay) but makes no sound. */
export class NullAudioManager implements AudioManager {
  private enabled = true;
  private suspended = false;
  readonly calls: { sound: OneShotSound; volume: number }[] = [];
  readonly spoken: SpokenLine[] = [];
  /** Last position the game reported. Null until it has reported one. */
  listener: { x: number; z: number } | null = null;
  /** Last ground speed the game reported. */
  pace = 0;
  private readonly loops = new Set<LoopSound>();

  play(sound: OneShotSound, volume = 1): void {
    if (!this.enabled || this.suspended) return;
    this.calls.push({ sound, volume });
    if (this.calls.length > 64) this.calls.shift();
  }

  speak(line: SpokenLine): void {
    if (!this.enabled || this.suspended) return;
    this.spoken.push(line);
    if (this.spoken.length > 16) this.spoken.shift();
  }

  isSpeaking(_id: string): boolean {
    return false;
  }

  startLoop(sound: LoopSound): void {
    if (!this.enabled) return;
    this.loops.add(sound);
  }

  stopLoop(sound: LoopSound): void {
    this.loops.delete(sound);
  }

  setListener(x: number, z: number): void {
    this.listener = { x, z };
  }

  setPace(speed: number): void {
    this.pace = speed;
  }

  stopAll(): void {
    this.loops.clear();
    this.stopSpeech();
  }

  stopSpeech(): void {
    this.spoken.length = 0;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.stopAll();
  }

  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
  }

  sampleStatus(): SampleStatus {
    return { total: 0, ready: 0, failed: 0 };
  }

  dispose(): void {
    this.stopAll();
  }

  get activeLoops(): readonly LoopSound[] {
    return [...this.loops];
  }
}
